"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Hero() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-item", {
        y: 16
      });
      gsap.to(".reveal-item", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: "power3.out"
      });
      gsap.set(".collage-frame", {
        scale: 0.96
      });
      gsap.to(".collage-frame", {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        stagger: 0.12,
        ease: "back.out(1.4)",
        delay: 0.2
      });
      gsap.to(".bubble-drift", {
        y: -12,
        duration: 7,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        stagger: {
          each: 1.2,
          from: "random"
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".reveal-item", ".collage-frame"], {
        opacity: 1,
        y: 0,
        scale: 1
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="28196" id="herocardiovascularconsultation" data-nav="light" className="bg-background text-foreground py-16 md:py-28 lg:py-36">
      <div ref={container} className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10 items-center">
          {}
          <div className="lg:col-span-7 relative overflow-hidden p-8 sm:p-12 lg:p-16 bg-secondary" style={{
          borderRadius: "48px"
        }}>
            {}
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
              <div className="bubble-drift absolute rounded-lg bg-background opacity-60" style={{
              width: "90px",
              height: "90px",
              top: "8%",
              right: "12%"
            }} />
              <div className="bubble-drift absolute rounded-lg bg-background opacity-55" style={{
              width: "50px",
              height: "50px",
              top: "35%",
              right: "6%"
            }} />
              <div className="bubble-drift absolute rounded-lg bg-background opacity-50" style={{
              width: "70px",
              height: "70px",
              bottom: "18%",
              right: "20%"
            }} />
            </div>

            <div className="relative z-10">
              <div className="reveal-item" style={{
              opacity: 0
            }}>
                <Badge className="rounded-lg px-4 py-1.5 text-xs font-semibold tracking-wider uppercase bg-background text-foreground">
                  Expert Cardiology Care
                </Badge>
              </div>

              <h1 className="reveal-item font-serif mt-6 text-foreground font-medium leading-[0.95]" style={{
              opacity: 0,
              fontSize: "clamp(2.75rem, 6.5vw, 5.75rem)",
              letterSpacing: "-0.03em"
            }}>
                World-Class <span className="italic font-serif">Cardiovascular</span> Care
              </h1>

              <p className="reveal-item mt-6 max-w-xl text-muted-foreground leading-relaxed" style={{
              opacity: 0,
              fontSize: "clamp(1.05rem, 1.2vw, 1.25rem)"
            }}>
                Dr. Rodolfo Carrillo-Jimenez provides advanced, physician-led heart health services in Delray Beach and Boynton Beach. Your heart deserves the best.
              </p>

              <div className="reveal-item mt-10 flex flex-wrap gap-4" style={{
              opacity: 0
            }}>
                <Button asChild size="lg" className="rounded-lg px-8 py-6 text-base font-semibold h-auto shadow-md hover:shadow-lg transition-all bg-primary text-primary-foreground">
                  <Link to="/patient-information/">Request Appointment</Link>
                </Button>
                <Button asChild size="lg" className="rounded-lg px-8 py-6 text-base font-semibold h-auto shadow-md hover:shadow-lg transition-all bg-accent text-accent-foreground">
                  <Link to="/about/">Meet Dr. Carrillo</Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5">
            <div className="relative w-full aspect-[4/5] max-w-md mx-auto lg:max-w-none">
              {}
              <div className="collage-frame absolute top-0 left-0 w-[78%] h-[68%] overflow-hidden shadow-xl" style={{
              opacity: 0,
              borderRadius: "48% 52% 40% 60% / 55% 45% 55% 45%",
              transform: "rotate(-3deg)"
            }}>
                <img src="/wp-content/uploads/2026/08/dr-carrillo-exam-room.jpg" alt="Rodolfo Carrillo-Jimenez, MD, FACC, leading cardiologist" className="w-full h-full object-cover" loading="eager" decoding="async" />
              </div>

              {}
              <div className="collage-frame absolute bottom-[4%] left-[6%] w-[42%] aspect-square rounded-lg overflow-hidden shadow-lg border-4 border-background" style={{
              opacity: 0
            }}>
                <img src="/react-current/images/site/cardiology_clinic_facility.webp" alt="Modern cardiology clinic facility in Delray Beach" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>

              {}
              <div className="collage-frame absolute bottom-[8%] right-0 w-[46%] aspect-square overflow-hidden shadow-lg" style={{
              opacity: 0,
              borderRadius: "32px",
              transform: "rotate(4deg)"
            }}>
                <img src="/react-current/images/site/cardiac_diagnostic_equipment.png" alt="Advanced cardiac diagnostic equipment for patient care" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
